import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CONTEST_ERROR,
  EMAIL_MAX_LENGTH,
  isValidEmail,
  pickLang,
  type ContestState,
  type EventTexts,
} from "@shared";
import { ApiFetchError, NETWORK_ERROR_CODE } from "../api/client";
import { currentLang } from "../i18n";
import { enterContest } from "./gameApi";

interface Props {
  texts: EventTexts;
  contest: ContestState;
  onEntered: () => void;
}

/**
 * Participation au tirage au sort, au bas de l'accueil.
 *
 * Trois états, et un seul visible à la fois :
 *   - VERROUILLÉ  tant que les deux missions ne sont pas terminées ;
 *   - FORMULAIRE  une fois déverrouillé ;
 *   - MERCI       après participation.
 *
 * L'état verrouillé s'affiche quand même, grisé : un concours qu'on ne voit
 * pas avant de l'avoir mérité ne donne envie de rien. Il annonce ce qu'il
 * faut faire pour l'ouvrir, sans rien demander.
 *
 * La carte n'apparaît pas du tout si l'admin n'a pas activé le concours —
 * c'est `contest.enabled`, décidé serveur.
 */
export default function ContestCard({ texts, contest, onEntered }: Props) {
  const { t } = useTranslation();
  const lang = currentLang();

  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!contest.enabled) return null;

  const titre = pickLang(texts.contest_title, lang);

  // AVOIR PARTICIPÉ passe AVANT le verrou, et l'ordre n'est pas indifférent.
  // Un joueur qui a déjà participé doit continuer à le voir, même si la règle
  // d'ouverture a changé depuis. C'est arrivé : la règle est passée de « les
  // deux expéditions terminées » à « complétées à 100 % », et un joueur déjà
  // inscrit au tirage revoyait la carte verrouillée — alors que sa
  // participation comptait toujours.
  if (contest.alreadyEntered) {
    return (
      <section className="contest contest--done">
        <p className="contest__badge">🎉</p>
        <h2 className="contest__title">{titre}</h2>
        <p className="contest__body">{t("contest.thanks")}</p>
        <p className="contest__body">{t("contest.thanksDeadline")}</p>
        <p className="contest__body">{t("contest.thanksNotice")}</p>
      </section>
    );
  }

  if (!contest.unlocked) {
    return (
      <section className="contest contest--locked">
        <p className="contest__badge">🔒</p>
        <h2 className="contest__title">{titre}</h2>
        <p className="contest__body">{t("contest.locked")}</p>
      </section>
    );
  }

  // Le serveur revalide tout : ces vérifications ne servent qu'à éviter un
  // aller-retour et à désactiver le bouton tant que la saisie est incomplète.
  const emailValide = isValidEmail(email.trim());
  const pretAEnvoyer = emailValide && consent && !busy;

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await enterContest(email.trim(), consent, lang);
      onEntered();
    } catch (raison) {
      setError(messageFor(raison));
    } finally {
      setBusy(false);
    }
  }

  /**
 * Découpe le texte du concours en lignes, pour pouvoir mettre les LOTS en
 * relief.
 *
 * Le texte vient de l'administration : c'est une zone de saisie libre, sans
 * mise en forme. Les lots y sont écrits avec une puce en début de ligne, et
 * c'est cette puce — rien d'autre — qui déclenche le décalage. Un admin qui
 * réécrit le texte sans puces obtient simplement un paragraphe ordinaire ;
 * rien ne casse.
 *
 * Chaque ligne devient un bloc, ce qui permet un RETRAIT PENDANT : si un lot
 * est trop long pour la largeur du téléphone, sa suite s'aligne sous le texte
 * et non sous la puce. Une simple indentation par espaces, elle, aurait sauté
 * au premier retour à la ligne.
 */
function enLignes(texte: string): React.ReactNode[] {
  return texte.split("\n").map((ligne, index) => {
    const cle = `${index}`;
    // Ligne vide : elle sépare les paragraphes. Un bloc vide de hauteur fixe
    // plutôt qu'un saut de ligne, puisque les lignes sont devenues des blocs.
    if (ligne.trim() === "") return <span key={cle} className="contest__saut" />;

    const estUnLot = ligne.trimStart().startsWith("•");
    return (
      <span key={cle} className={`contest__ligne ${estUnLot ? "contest__lot" : ""}`}>
        {ligne}
      </span>
    );
  });
}

/** Le message vient du CODE renvoyé par le serveur, jamais de son texte. */
  function messageFor(raison: unknown): string {
    const code = raison instanceof ApiFetchError ? raison.code : NETWORK_ERROR_CODE;
    switch (code) {
      case CONTEST_ERROR.duplicate:
        return t("contest.error.duplicate");
      case CONTEST_ERROR.invalidEmail:
        return t("contest.error.invalidEmail");
      case CONTEST_ERROR.consentRequired:
        return t("contest.error.consentRequired");
      case CONTEST_ERROR.locked:
        return t("contest.error.locked");
      case CONTEST_ERROR.disabled:
        return t("contest.error.disabled");
      case NETWORK_ERROR_CODE:
        return t("contest.error.offline");
      default:
        return t("contest.error.unexpected");
    }
  }

  return (
    <section className="contest">
      <p className="contest__badge">🎁</p>
      <h2 className="contest__title">{titre}</h2>
      <p className="contest__body">{enLignes(pickLang(texts.contest_body, lang))}</p>

      {error !== null && (
        <p className="form__error" role="alert">
          {error}
        </p>
      )}

      <form className="form" onSubmit={(e) => void submit(e)}>
        {/* La case AVANT le champ : on lit ce à quoi on consent, puis on donne
            son adresse. L'inverse reviendrait à demander d'abord et à
            expliquer ensuite. */}
        <label className="form__check contest__consent">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => {
              setConsent(e.target.checked);
            }}
          />
          <span>
            {t("contest.consentLabel")}{" "}
            {/* Ouvre dans un nouvel onglet : un adulte qui lit la page ne doit
                pas perdre l'adresse déjà saisie. */}
            <a
              className="contest__link"
              href={t("contest.consentPath")}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("contest.consentLink")}
            </a>
          </span>
        </label>

        <label className="form__label" htmlFor="contest-email">
          {t("contest.emailLabel")}
        </label>
        <input
          id="contest-email"
          className="form__input"
          type="email"
          inputMode="email"
          autoComplete="email"
          maxLength={EMAIL_MAX_LENGTH}
          placeholder={t("contest.emailPlaceholder")}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError(null);
          }}
        />
        {/* Ne reproche rien tant que le champ est vide ou en cours de frappe :
            un message d'erreur qui apparaît à la première lettre est pénible. */}
        {email.trim() !== "" && !emailValide && (
          <p className="form__hint contest__hint">{t("contest.emailHint")}</p>
        )}

        <button
          type="submit"
          className="button button--primary button--full button--big"
          disabled={!pretAEnvoyer}
        >
          {busy ? t("contest.submitting") : t("contest.submit")}
        </button>
      </form>
    </section>
  );
}
